import { useState } from "react";
import { ClearRounded } from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { selectedPurchaseOrderPath } from "components/PimDashboard/data";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import PoConfirmDialog from "../CreatePurchaseOrder/PoConfirmDialog";
import { poUrlKey } from "../CreatePurchaseOrder/data";
import { PoGinesysSyncBadge, poColors, poPrimaryBtnSx, poSecondaryBtnSx } from "../CreatePurchaseOrder/poUi";
import { poSyncLogEventTone, reopenPurchaseOrderForEditApi, retryPurchaseOrderSyncApi } from "./data";

const formatJson = (value) => {
	if (value == null || value === "") return "—";
	try {
		const parsed = typeof value === "string" ? JSON.parse(value) : value;
		return JSON.stringify(parsed, null, 2);
	} catch {
		return String(value);
	}
};

const formatDateTime = (value) => {
	if (!value) return "—";
	const d = new Date(value);
	return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

const DetailRow = ({ label, children }) => (
	<Box
		sx={{
			display: "grid",
			gridTemplateColumns: { xs: "1fr", sm: "160px 1fr" },
			gap: { xs: 0.25, sm: 1.5 },
			alignItems: "start",
			py: 0.75,
		}}>
		<Typography sx={{ fontSize: 12, fontWeight: 700, color: poColors.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
			{label}
		</Typography>
		<Box sx={{ fontSize: 13, color: poColors.ink, wordBreak: "break-word" }}>{children}</Box>
	</Box>
);

const JsonBlock = ({ value }) => (
	<Box
		component="pre"
		sx={{
			m: 0,
			p: 1.25,
			maxHeight: 280,
			overflow: "auto",
			borderRadius: "10px",
			border: `1px solid ${poColors.line}`,
			bgcolor: poColors.panelBg,
			fontSize: 12,
			lineHeight: 1.45,
			fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
			whiteSpace: "pre-wrap",
			wordBreak: "break-word",
		}}>
		{formatJson(value)}
	</Box>
);

const EventBadge = ({ value }) => {
	const key = value || "QUEUED";
	const tone = poSyncLogEventTone[key] || poSyncLogEventTone.QUEUED;
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				px: 1.1,
				py: 0.35,
				borderRadius: "8px",
				fontSize: 12,
				fontWeight: 700,
				letterSpacing: "0.04em",
				textTransform: "uppercase",
				color: tone.color,
				bgcolor: tone.bgcolor,
				width: "fit-content",
			}}>
			{key}
		</Box>
	);
};

const SyncLogDetailModal = ({ open, onClose, row, onActionComplete }) => {
	const navigate = useNavigate();
	const [busy, setBusy] = useState(false);
	const [actionError, setActionError] = useState("");
	const [actionMessage, setActionMessage] = useState("");
	const [confirmAction, setConfirmAction] = useState(null); // "retry" | "reopen" | null

	if (!row) return null;

	const canRecover =
		(row.event_type === "FAILED" || row.ginesys_sync === "FAILED") &&
		row.purchase_order_id &&
		!row.ginesys_po_id;

	const runRetry = async () => {
		setConfirmAction(null);
		setBusy(true);
		setActionError("");
		setActionMessage("");
		try {
			const res = await retryPurchaseOrderSyncApi(row.purchase_order_id, { outbox_id: row.outbox_id });
			setActionMessage(res.data?.message || "Sync re-queued");
			if (typeof onActionComplete === "function") onActionComplete({ action: "retry", result: res.data?.result });
		} catch (err) {
			setActionError(parseErrorMessage(err));
		} finally {
			setBusy(false);
		}
	};

	const runReopen = async () => {
		setConfirmAction(null);
		setBusy(true);
		setActionError("");
		setActionMessage("");
		try {
			const res = await reopenPurchaseOrderForEditApi(row.purchase_order_id, { outbox_id: row.outbox_id });
			setActionMessage(res.data?.message || "PO reopened");
			if (typeof onActionComplete === "function") onActionComplete({ action: "reopen", result: res.data?.result });
			onClose();
			navigate(`/${selectedPurchaseOrderPath}?${poUrlKey}=${row.purchase_order_id}`);
		} catch (err) {
			setActionError(parseErrorMessage(err));
		} finally {
			setBusy(false);
		}
	};

	return (
		<>
			<Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth scroll="paper">
				<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
					<Stack spacing={0.25}>
						<Typography variant="h6" sx={{ fontSize: 18, fontWeight: 700 }}>
							PO Sync Log #{row.id}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{row.po_no || `PO id ${row.purchase_order_id}`}
						</Typography>
					</Stack>
					<IconButton onClick={onClose} size="small" aria-label="Close" disabled={busy}>
						<ClearRounded />
					</IconButton>
				</DialogTitle>
				<Divider />
				<DialogContent sx={{ pt: 1.5, pb: 2.5 }}>
					{canRecover && (
						<Alert severity="warning" sx={{ mb: 1.5 }}>
							Ginesys sync failed. Choose <strong>Retry sync</strong> (same payload) or{" "}
							<strong>Edit &amp; re-publish</strong> (unlock draft, fix data, publish again).
						</Alert>
					)}
					{actionError && (
						<Alert severity="error" sx={{ mb: 1.5 }}>
							{actionError}
						</Alert>
					)}
					{actionMessage && (
						<Alert severity="success" sx={{ mb: 1.5 }}>
							{actionMessage}
						</Alert>
					)}

					<Stack spacing={0.25} divider={<Divider flexItem sx={{ borderColor: poColors.line }} />}>
						<DetailRow label="Event">
							<EventBadge value={row.event_type} />
						</DetailRow>
						<DetailRow label="Ginesys Sync">
							<PoGinesysSyncBadge value={row.ginesys_sync} />
						</DetailRow>
						<DetailRow label="PO No">{row.po_no || "—"}</DetailRow>
						<DetailRow label="PO Id">{row.purchase_order_id ?? "—"}</DetailRow>
						<DetailRow label="Outbox Id">{row.outbox_id ?? "—"}</DetailRow>
						<DetailRow label="Ginesys PO Id">{row.ginesys_po_id ?? "—"}</DetailRow>
						<DetailRow label="Scheme Doc No">{row.ginesys_scheme_doc_no || "—"}</DetailRow>
						<DetailRow label="Triggered By">{row.triggered_by || "—"}</DetailRow>
						<DetailRow label="Created">{formatDateTime(row.created_at)}</DetailRow>
						<DetailRow label="Updated">{formatDateTime(row.updated_at)}</DetailRow>
						<DetailRow label="Error">
							<Typography
								sx={{
									fontSize: 13,
									color: row.error_message ? poColors.danger : poColors.ink,
									whiteSpace: "pre-wrap",
								}}>
								{row.error_message || "—"}
							</Typography>
						</DetailRow>
						<DetailRow label="Request">
							<JsonBlock value={row.request_payload} />
						</DetailRow>
						<DetailRow label="Response">
							<JsonBlock value={row.response_payload} />
						</DetailRow>
					</Stack>
				</DialogContent>
				{canRecover && (
					<DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap", justifyContent: "flex-start" }}>
						<Button
							variant="contained"
							disabled={busy}
							onClick={() => setConfirmAction("retry")}
							startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
							sx={poPrimaryBtnSx}>
							Retry sync
						</Button>
						<Button
							variant="outlined"
							disabled={busy}
							onClick={() => setConfirmAction("reopen")}
							sx={poSecondaryBtnSx}>
							Edit &amp; re-publish
						</Button>
						<Button variant="text" disabled={busy} onClick={onClose}>
							Close
						</Button>
					</DialogActions>
				)}
			</Dialog>

			<PoConfirmDialog
				open={confirmAction === "retry"}
				title="Retry Ginesys sync?"
				message="This will re-queue the same published payload. Cron will call Ginesys Create PO again."
				confirmLabel="Retry sync"
				loading={busy}
				onCancel={() => setConfirmAction(null)}
				onConfirm={runRetry}
			/>
			<PoConfirmDialog
				open={confirmAction === "reopen"}
				title="Edit & re-publish?"
				message="This PO will reopen as a draft so you can fix data and publish again. The failed outbox will not be retried."
				confirmLabel="Reopen as draft"
				loading={busy}
				onCancel={() => setConfirmAction(null)}
				onConfirm={runReopen}
			/>
		</>
	);
};

export default SyncLogDetailModal;
